import { useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { FormProvider, RHFSelect, RHFTextField } from '../../../components/hook-form';

import { updateRepairRecord } from '../store/maintenanceActions';

const ITEM_TYPE = [
  {
    id: 1,
    label: 'Part',
    value: 'PART',
  },
  {
    id: 2,
    label: 'Service',
    value: 'SERVICE',
  },
];

EditRepair.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  repairRecord: PropTypes.object,
};

export default function EditRepair({ open, handleClose, repairRecord }) {
  const dispatch = useDispatch();
  const [image, setImage] = useState(null);
  const [selectedType, setSelectedType] = useState(repairRecord?.item_type || 'PART');

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setValue('partPhoto', file);
    setImage(file);
  };

  const handleSelectedType = (event) => {
    const type = event.target.value;
    setSelectedType(type);
    setValue('itemType', type);
  };

  const SPAcceptanceSchema = Yup.object().shape({
    // maintenanceId: Yup.string().required('Maintenance id is required'),
    itemType: Yup.string().required('Type id is required'),
    partName: Yup.string().required('Used part name is required'),
    serialNumber: Yup.string(),
    warranty: Yup.string(),
    description: Yup.string(),
    quantity: Yup.string(),
    cost: Yup.number().positive().required(' Estimated cost is required'),
    // expenses: Yup.number().positive(),
    includesExpenses: Yup.boolean(),
  });

  const defaultValues = {
    itemType: repairRecord?.item_type || '',
    quantity: repairRecord?.quantity || '',
    partName: repairRecord?.part_name || '',
    serialNumber: repairRecord?.part_serial_number || '',
    warranty: repairRecord?.warranty || '',
    cost: repairRecord?.estimated_cost || '',
    expenses: repairRecord?.expenses || '',
    includesExpenses: repairRecord?.includes_expenses || false,
    description: repairRecord?.description || '',
    // partPhoto: repairRecord?.part_photo || '',
    partPhoto: '',
  };

  const methods = useForm({
    resolver: yupResolver(SPAcceptanceSchema),
    defaultValues,
  });

  const {
    // reset,
    // control,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  /**
   * Only append fields that actually have a value.
   *
   * `formData.append(k, undefined)` sends the literal string "undefined", and
   * appending an empty `part_photo` made the API treat the edit as "replace the
   * photo", which wiped the existing image on every save.
   */
  const buildFormData = (data, extra = {}) => {
    const formData = new FormData();
    Object.entries(extra).forEach(([key, value]) => formData.append(key, value));
    const fields = {
      item_type: data.itemType,
      quantity: data.itemType === 'PART' ? data.quantity : '',
      part_name: data.partName,
      part_serial_number: data.serialNumber,
      warranty: data.warranty,
      estimated_cost: data.cost,
      includes_expenses: data.includesExpenses,
      expenses: data.expenses,
      description: data.description,
    };
    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') formData.append(key, value);
    });
    if (data.partPhoto instanceof File) formData.append('part_photo', data.partPhoto);
    return formData;
  };

  const onSubmit = async (data) => {
    const result = await dispatch(updateRepairRecord({ id: repairRecord?.id, payload: buildFormData(data) }));
    if (result?.ok) handleClose(true);
  };
  const ChannelCheckbox = ({ name, label }) => {
    const { control } = useFormContext();

    return (
      <Controller
        render={({ field }) => (
          <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label={label} />
        )}
        control={control}
        name={name}
        defaultValue={false}
      />
    );
  };
  return (
    <Dialog
      maxWidth="md"
      open={open}
      fullWidth
      keepMounted
      onClose={() => handleClose(false)}
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle>Edit Repair Item</DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Stack spacing={2}>
            <RHFSelect
              fullWidth
              name="itemType"
              label="Item Type"
              value={selectedType}
              onChange={(e) => handleSelectedType(e)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
            >
              {ITEM_TYPE?.map((option) => (
                <MenuItem key={option.id} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>
            <RHFTextField name="partName" label="Part/Service Name" />
            {selectedType === 'PART' && (
              <>
                <RHFTextField name="serialNumber" label="Serial Number" />
                <RHFTextField name="warranty" label="Warranty(Year)" placeholder="Optional" />
                <RHFTextField type="number" name="quantity" label="Quantity" />
              </>
            )}
            <RHFTextField type="number" name="cost" label="Estimated Cost" />
            {/* <RHFTextField type="number" name="expenses" label="Expense Cost" /> */}
            <RHFTextField
              type="file"
              name="partPhoto"
              value={null}
              onChange={handleFileUpload}
              label={image ? image.name : 'Attachment'}
            />
            <RHFTextField name="description" label="Description" placeholder="Optional" />
            <Stack direction="row" spacing={1}>
              <ChannelCheckbox name="includesExpenses" label="Estimated Cost  includes Expenses? " />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" variant="contained" color="error" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <LoadingButton type="submit" size="small" variant="contained" loading={isSubmitting}>
            Save
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
